import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/WorkerDetail.css';
import { 
  getCompanyEmployees, 
  updateEmployeeInfo, 
  getMonthlyAttendanceData,
  getCompanyMonthlyAttendance,
  getHolidays,
  downloadCompanyMonthlyExcel
} from '../utils/api';
import Dialog from '../components/Dialog';

function WorkerDetail() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [worker, setWorker] = useState({
    name: '',
    entryDate: '',
    birthDate: '',
    salary: '',
    phone: '',
    noticeStatus: ''
  });
  
  // 수정 모드 상태 관리
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({...worker});
  
  // 현재 표시되는 월 상태 관리
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // 출퇴근 데이터 상태
  const [attendanceData, setAttendanceData] = useState(null);
  
  // 근무 데이터 (UI용)
  const [workData, setWorkData] = useState({
    regularDays: 0,
    lateDays: 0,
    earlyLeaveDays: 0,
    workHours: 0,
    overtimeHours: 0,
    regularPay: 0,
    overtimePay: 0,
    totalPay: 0
  });
  
  // 날짜별 근무 기록
  const [workStatus, setWorkStatus] = useState({});
  
  // 공휴일 데이터 상태 추가
  const [holidays, setHolidays] = useState([]);
  
  // 다이얼로그 상태
  const [dialog, setDialog] = useState({
    isOpen: false,
    type: '',
    message: ''
  });
  
  // 회사 전체 출퇴근 데이터 상태 추가
  const [companyAttendanceData, setCompanyAttendanceData] = useState(null);
  
  // 다이얼로그 닫기 함수
  const closeDialog = () => {
    setDialog({ ...dialog, isOpen: false });
  };
  
  // API에서 데이터 가져오기
  useEffect(() => {
    // 월이 변경될 때마다 workStatus와 workData 모두 초기화
    setWorkStatus({});
    setWorkData({
      regularDays: 0,
      lateDays: 0,
      earlyLeaveDays: 0,
      workHours: 0,
      overtimeHours: 0,
      regularPay: 0,
      overtimePay: 0,
      totalPay: 0
    });
    fetchData();
  }, [id, currentMonth]);
  
  // 데이터 가져오기 함수
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // 사용자 정보 가져오기
      const employeeResponse = await getCompanyEmployees(id);
      
      if (!employeeResponse.employees || employeeResponse.employees.length === 0) {
        throw new Error('사용자 정보를 찾을 수 없습니다.');
      }
      
      const userData = employeeResponse.employees[0];
      
      // 로컬 스토리지에서 회사 코드 가져오기
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const companyCode = userInfo.middleCompanyCode;
      
      if (!companyCode) {
        throw new Error('회사 코드를 찾을 수 없습니다.');
      }
      
      setWorker({
        name: userData.name || '',
        entryDate: userData.hire_date || '',
        birthDate: userData.birth || '',
        salary: `시급 ${userData.wage?.toLocaleString() || 0}원`,
        phone: userData.phone || '',
        noticeStatus: 'O'
      });
      
      setEditData({
        name: userData.name || '',
        entryDate: userData.hire_date || '',
        birthDate: userData.birth || '',
        salary: userData.wage?.toLocaleString() || '0',
        phone: userData.phone || '',
        noticeStatus: 'O'
      });
      
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      
      // 공휴일 데이터 가져오기
      try {
        const holidaysResponse = await getHolidays(year, month);
        if (holidaysResponse && holidaysResponse.holidays) {
          setHolidays(holidaysResponse.holidays);
        }
      } catch (holidaysError) {
        console.error('공휴일 데이터 로드 오류:', holidaysError);
        // 공휴일 로드 실패해도 다른 데이터는 계속 로드
      }
      
      // 직원 출퇴근 데이터 가져오기
      const attendanceResponse = await getCompanyMonthlyAttendance(
        companyCode, 
        year, 
        month,
        userData.email
      );
      
      console.log('직원 출퇴근 데이터:', attendanceResponse);
      
      if (attendanceResponse.status === 'success' && attendanceResponse.data) {
        const data = attendanceResponse.data;
        
        // 월별 출퇴근 데이터 조회 API 응답 처리
        const responseYear = data.employee_summaries && data.employee_summaries[0]?.year;
        const responseMonth = data.employee_summaries && data.employee_summaries[0]?.month;
        
        const currentYear = currentMonth.getFullYear();
        const currentMonthNum = currentMonth.getMonth() + 1;
        
        // 응답의 연월이 현재 선택된 연월과 일치할 때만 데이터 설정
        if (responseYear === currentYear && responseMonth === currentMonthNum) {
          setAttendanceData(data);
          
          // employee_summaries에서 해당 직원 정보 가져오기
          if (data.employee_summaries && data.employee_summaries.length > 0) {
            const employeeSummary = data.employee_summaries[0];
            
            // 근무 데이터 업데이트 (UI용)
            const regularDays = employeeSummary.work_days || 0;
            let normalCheckInDays = 0;
            let normalCheckOutDays = 0;
            let lateDays = 0;
            let earlyLeaveDays = 0;
            
            // attendances에서 출퇴근 상태별 횟수 계산
            if (data.attendances) {
              data.attendances.forEach(record => {
                if (record.check_in_status === "출근") {
                  normalCheckInDays++;
                }
                if (record.check_in_status === "지각") {
                  lateDays++;
                }
                if (record.check_out_status === "퇴근") {
                  normalCheckOutDays++;
                }
                if (record.check_out_status === "조퇴") {
                  earlyLeaveDays++;
                }
              });
            }
            
            setWorkData({
              regularDays: normalCheckInDays,
              normalCheckOutDays: normalCheckOutDays,
              lateDays: lateDays,
              earlyLeaveDays: earlyLeaveDays,
              workHours: employeeSummary.total_work_hours || 0,
              overtimeHours: employeeSummary.total_overtime_hours || 0,
              // API 응답 값 사용
              regularPay: employeeSummary.regular_pay || 0,
              overtimePay: employeeSummary.special_pay || 0,  // special_pay로 변경
              totalPay: employeeSummary.total_pay || 0,
              weeklyHolidayPay: employeeSummary.weekly_holiday_pay || 0,
              socialInsurance: employeeSummary.social_insurance_amount || 0,
              netPay: Math.round((employeeSummary.net_pay || 0) / 10) * 10  // 10원 단위 반올림
            });
            
            // 날짜별 근무 기록 처리 (달력용)
            if (data.employee_summaries[0].daily_records && data.employee_summaries[0].daily_records.length > 0) {
              const tempWorkStatus = {};
              
              // 일별 기록에서 달력 데이터 생성
              data.employee_summaries[0].daily_records.forEach(record => {
                const date = new Date(record.date);
                const day = date.getDate();
                
                // 관련된 attendance 기록 찾기
                const attendanceRecord = data.attendances.find(att => att.date === record.date);
                let status = "출근";
                let hasLateOrEarly = false;  // 지각 또는 조퇴 여부
                
                if (attendanceRecord) {
                  if (attendanceRecord.check_in_status === "지각") {
                    status = "지각";
                    hasLateOrEarly = true;
                  } else if (attendanceRecord.check_out_status === "조퇴") {
                    status = "조퇴";
                    hasLateOrEarly = true;
                  }
                }
                
                // 일별 급여 계산 - API에서 제공하는 값 사용
                const dailyPay = (record.regular_pay || 0) + (record.special_pay || 0);
                
                tempWorkStatus[day] = {
                  type: status,
                  hours: `${record.work_hours}h`,
                  overtime: record.overtime_hours > 0 ? `(${record.overtime_hours})` : "",  // "+" 기호 제거
                  pay: dailyPay,
                  hasLateOrEarly: hasLateOrEarly  // 지각 또는 조퇴 여부 추가
                };
              });
              
              setWorkStatus(tempWorkStatus);
            }
          }
        } else {
          console.log(`데이터 없음: ${currentYear}년 ${currentMonthNum}월`);
          // 데이터가 없는 경우 상태를 비워둠
          setAttendanceData(null);
        }
        
        // 회사 전체 출퇴근 데이터 가져오기 (이메일 파라미터 추가)
        const companyAttendanceResponse = await getCompanyMonthlyAttendance(
          companyCode, 
          year, 
          month,
          userData.email
        );
        console.log('특정 직원 출퇴근 데이터(회사 API):', companyAttendanceResponse);
        
        if (companyAttendanceResponse.status === 'success' && companyAttendanceResponse.data) {
          setCompanyAttendanceData(companyAttendanceResponse.data);
        }
        
      }
      
    } catch (err) {
      console.error('데이터 로드 오류:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // 이전 달로 이동
  const goToPreviousMonth = () => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  };

  // 다음 달로 이동
  const goToNextMonth = () => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  };
  
  // 달력 날짜 생성
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const lastDate = new Date(year, month + 1, 0).getDate();
    
    const calendarData = [];
    let week = Array(7).fill(null);
    
    // 공휴일 날짜 매핑 생성
    const holidayDates = {};
    holidays.forEach(holiday => {
      if (holiday.is_holiday) {
        // 날짜 형식에서 일(day)만 추출 (예: "2025-05-05" -> 5)
        const day = parseInt(holiday.date.split('-')[2]);
        holidayDates[day] = holiday.date_name;
      }
    });
    
    for (let i = 0; i < firstDay; i++) {
      week[i] = null;
    }
    
    for (let date = 1; date <= lastDate; date++) {
      const dayIndex = (firstDay + date - 1) % 7;
      week[dayIndex] = {
        date,
        isToday: new Date().getDate() === date && 
                new Date().getMonth() === month && 
                new Date().getFullYear() === year,
        isHoliday: holidayDates[date] !== undefined,
        holidayName: holidayDates[date]
      };
      
      if (dayIndex === 6 || date === lastDate) {
        calendarData.push([...week]);
        week = Array(7).fill(null);
      }
    }
    
    return calendarData;
  };
  
  const calendarWeeks = generateCalendarDays();

  // 편집 모드 토글 함수 추가
  const toggleEditMode = async () => {
    if (isEditing) {
      try {
        // 변경된 데이터만 업데이트 데이터 객체에 포함
        const updateData = {};
        
        // 이름이 변경된 경우
        if (editData.name !== worker.name) {
          updateData.name = editData.name;
        }
        
        // 입사일이 변경된 경우
        if (editData.entryDate !== worker.entryDate) {
          updateData.hire_date = editData.entryDate;
        }
        
        // 생년월일이 변경된 경우
        if (editData.birthDate !== worker.birthDate) {
          updateData.birth = editData.birthDate;
        }
        
        // 임금이 변경된 경우 (시급 부분만 숫자로 변환)
        if (editData.salary !== worker.salary.replace(/시급 |원/g, '')) {
          updateData.wage = parseInt(editData.salary.replace(/,/g, ''));
        }
        
        // 전화번호가 변경된 경우
        if (editData.phone !== worker.phone) {
          updateData.phone = editData.phone;
        }
        
        // 변경된 데이터가 있는 경우에만 API 호출
        if (Object.keys(updateData).length > 0) {
          // 로딩 상태 설정
          setLoading(true);
          
          // API 호출
          const response = await updateEmployeeInfo(id, updateData);
          
          if (response) {
            // 성공 시 상태 업데이트
            setWorker({
              name: response.name || '',
              entryDate: response.hire_date || '',
              birthDate: response.birth || '',
              salary: `시급 ${editData.salary}원`,
              phone: response.phone || '',
              noticeStatus: worker.noticeStatus
            });
            
            // 편집 모드 종료
            setIsEditing(false);
            
            // 성공 다이얼로그 표시
            setDialog({
              isOpen: true,
              type: Dialog.TYPES.EMPLOYEE_EDIT_SUCCESS
            });
            return;
          }
        } else {
          // 변경된 데이터가 없는 경우 원래 상태 유지
          setIsEditing(false);
          return;
        }
      } catch (error) {
        // 오류 처리
        console.error('직원 정보 수정 중 오류:', error);
        
        // 오류 다이얼로그 표시
        setDialog({
          isOpen: true,
          type: Dialog.TYPES.ERROR,
          message: `직원 정보 수정 중 오류가 발생했습니다: ${error.message}`
        });
        
        return;
      } finally {
        // 로딩 상태 해제
        setLoading(false);
      }
    } else {
      // 편집 모드로 들어갈 때 현재 데이터 복사
      setEditData({...worker});
      setIsEditing(true);
    }
  };

  // 입력 필드 변경 핸들러 추가
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Excel 다운로드 핸들러 함수 추가
  const handleExcelDownload = async () => {
    try {
      setLoading(true);
      
      // 로컬 스토리지에서 회사 코드 가져오기
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const companyCode = userInfo.middleCompanyCode;
      
      if (!companyCode) {
        throw new Error('회사 코드를 찾을 수 없습니다.');
      }
      
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      
      // 특정 직원의 이메일을 파라미터로 전달
      const blob = await downloadCompanyMonthlyExcel(
        companyCode, 
        year, 
        month,
        id // id는 useParams에서 가져온 직원 이메일
      );
      
      // 파일 다운로드 로직
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${year}년_${month}월_${worker.name}_근로정보.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Excel 다운로드 오류:', error);
      // 다이얼로그로 오류 메시지 표시
      setDialog({
        isOpen: true,
        type: Dialog.TYPES.ERROR,
        message: `Excel 다운로드 중 오류가 발생했습니다: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loadingIndicator">데이터를 불러오는 중...</div>;
  if (error) return <div className="errorMessage">오류: {error}</div>;

  return (
    <div className="mainContent">
      {/* 상단 헤더: 사원 이름과 버튼들 */}
      <div className="headerSection">
        <div className="nameAndEditSection">
          <h2 className="workerName">
            <span className="namePart">{worker.name}</span>
            <span className="nimPart"> 님</span>
          </h2>
          {isEditing ? (
            <button className="saveButton" onClick={toggleEditMode}>
              <span className="okIcon">OK</span>
              수정완료하기
            </button>
          ) : (
            <button className="editInfoButton" onClick={toggleEditMode}>
              <span className="buttonIcon">✎</span> 정보 수정하기
            </button>
          )}
        </div>
        <button className="exportButton" onClick={handleExcelDownload}>
          <span className="buttonIcon">↓</span> Excel로 내려받기
        </button>
      </div>
      
      <div className="detailLayout">
        {/* 왼쪽 영역: 개인 정보 및 근무 요약 */}
        <div className="leftSection">
          {/* 개인 정보 테이블 */}
          <div className="infoTable">
            <div className="infoRow">
              <div className="infoLabel">이름</div>
              <div className="infoValue">
                {isEditing ? (
                  <input 
                    type="text" 
                    name="name" 
                    value={editData.name} 
                    onChange={handleInputChange}
                    className="editInput"
                  />
                ) : (
                  worker.name
                )}
              </div>
            </div>
            <div className="infoRow">
              <div className="infoLabel">입사일</div>
              <div className="infoValue">
                {isEditing ? (
                  <input 
                    type="text" 
                    name="entryDate" 
                    value={editData.entryDate} 
                    onChange={handleInputChange}
                    className="editInput"
                  />
                ) : (
                  worker.entryDate
                )}
              </div>
            </div>
            <div className="infoRow">
              <div className="infoLabel">생년월일</div>
              <div className="infoValue">
                {isEditing ? (
                  <input 
                    type="text" 
                    name="birthDate" 
                    value={editData.birthDate} 
                    onChange={handleInputChange}
                    className="editInput"
                  />
                ) : (
                  worker.birthDate
                )}
              </div>
            </div>
            <div className="infoRow">
              <div className="infoLabel">임금</div>
              <div className="infoValue">
                {isEditing ? (
                  <div className="salaryInputWrapper">
                    <span className="salaryPrefix">시급</span>
                    <input 
                      type="text" 
                      name="salary" 
                      value={editData.salary.replace(/시급 |원/g, '')} 
                      onChange={handleInputChange}
                      className="editInput salaryInput"
                    />
                    <span className="salarySuffix">원</span>
                  </div>
                ) : (
                  worker.salary
                )}
              </div>
            </div>
            <div className="infoRow">
              <div className="infoLabel">전화번호</div>
              <div className="infoValue">
                {isEditing ? (
                  <input 
                    type="text" 
                    name="phone" 
                    value={editData.phone} 
                    onChange={handleInputChange}
                    className="editInput"
                  />
                ) : (
                  worker.phone
                )}
              </div>
            </div>
            <div className="infoRow">
              <div className="infoLabel">공지사항 동의 여부</div>
              <div className="infoValue">
                {isEditing ? (
                  <select 
                    name="noticeStatus" 
                    value={editData.noticeStatus} 
                    onChange={handleInputChange}
                    className="editInput noticeSelect"
                  >
                    <option value="O">O</option>
                    <option value="X">X</option>
                  </select>
                ) : (
                  worker.noticeStatus
                )}
              </div>
            </div>
          </div>

          {/* 월별 근무 요약 제목 */}
          <h3 className="summaryTitle">{`${currentMonth.getFullYear()}년 ${currentMonth.getMonth() + 1}월`}</h3>
          
          {/* 요약 테이블 */}
          <div className="summaryTable">
            <div className="summaryRow">
              <div className="summaryLabel">정상출근</div>
              <div className="summaryValue">{workData.regularDays}</div>
              <div className="divider"></div>
              <div className="summaryLabel">근무시간</div>
              <div className="summaryValue">
                {workData.workHours}
                <span className="overtimeHours">({workData.overtimeHours})</span> 시간
              </div>
            </div>
            <div className="summaryRow">
              <div className="summaryLabel">정상퇴근</div>
              <div className="summaryValue">{workData.normalCheckOutDays}</div>
              <div className="divider"></div>
              <div className="summaryLabel">규정근무수당</div>
              <div className="summaryValue summarySpaced">{workData.regularPay.toLocaleString()} 원</div>
            </div>
            <div className="summaryRow">
              <div className="summaryLabel">자각</div>
              <div className="summaryValue">{workData.lateDays}</div>
              <div className="divider"></div>
              <div className="summaryLabel">특별근무수당</div>
              <div className="summaryValue summarySpaced">{workData.overtimePay.toLocaleString()} 원</div>
            </div>
            <div className="summaryRow">
              <div className="summaryLabel">조퇴</div>
              <div className="summaryValue">{workData.earlyLeaveDays}</div>
              <div className="divider"></div>
              <div className="summaryLabel totalLabel">총 지급액</div>
              <div className="summaryValue totalValue">{workData.netPay.toLocaleString()} 원</div>
            </div>
          </div>
        </div>
        
        {/* 오른쪽 영역: 달력 */}
        <div className="rightSection halfCalendar">
          <div className="calendar">
            <div className="calendarHeader">
              <button className="monthNavButton" onClick={goToPreviousMonth}>{'<'}</button>
              <h3 className="calendarTitle">{currentMonth.getFullYear()}년 {currentMonth.getMonth() + 1}월</h3>
              <button className="monthNavButton" onClick={goToNextMonth}>{'>'}</button>
            </div>
            
            <div className="weekdayHeader">
              <div className="weekday sunday">일</div>
              <div className="weekday">월</div>
              <div className="weekday">화</div>
              <div className="weekday">수</div>
              <div className="weekday">목</div>
              <div className="weekday">금</div>
              <div className="weekday saturday">토</div>
            </div>
            
            <div className="calendarBody">
              {calendarWeeks.map((week, weekIndex) => (
                <div key={weekIndex} className="weekRow">
                  {week.map((day, dayIndex) => (
                    <div key={`${weekIndex}-${dayIndex}`} className={`day ${dayIndex === 0 ? 'sunday' : dayIndex === 6 ? 'saturday' : ''}`}>
                      {day && (
                        <>
                          <div className={`dayNumber ${day.isToday ? 'today' : ''} ${day.isHoliday ? 'holiday' : ''}`}>
                            {day.date}
                          </div>
                          {day.isHoliday && (
                            <div className="holidayName">{day.holidayName}</div>
                          )}
                          {workStatus[day.date] && (
                            <div className="dayContent">
                              <div className={`hoursTag ${workStatus[day.date].hasLateOrEarly ? 'lateOrEarly' : 'normal'}`}>
                                {workStatus[day.date].hours}
                                <span className="overtimeDisplay">{workStatus[day.date].overtime}</span>
                              </div>
                              <div className="statusText">{workStatus[day.date].type}</div>
                              <div className="payAmount">{workStatus[day.date].pay.toLocaleString()}</div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* 개별 직원 출퇴근 데이터 출력 */}
      {attendanceData && (
        <div className="dataSection">
          <h3>직원 출퇴근 데이터</h3>
          <pre className="jsonData">{JSON.stringify(attendanceData, null, 2)}</pre>
        </div>
      )}
      
      {/* 회사 전체 출퇴근 데이터 출력 (추가) */}
      {companyAttendanceData && (
        <div className="dataSection">
          <h3>회사 전체 출퇴근 데이터</h3>
          <pre className="jsonData">{JSON.stringify(companyAttendanceData, null, 2)}</pre>
        </div>
      )}
      
      {/* 다이얼로그 컴포넌트 */}
      <Dialog 
        isOpen={dialog.isOpen}
        type={dialog.type}
        message={dialog.message}
        onClose={closeDialog}
        onConfirm={closeDialog}
      />
    </div>
  );
}

export default WorkerDetail; 